// @ts-strict-ignore
import React, { memo, startTransition, useCallback, useEffect, useRef, useState } from 'react'
import ButtonStack from 'components/ButtonStack'
import ResponsiveDialog from 'components/ResponsiveDialog'
import TextArea from 'components/TextArea'
import { People, Send } from '@mui/icons-material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { useUploadFile } from 'hooks/filestack'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useChat } from 'hooks/chat'
import { ChatThreadActions_Thread$key } from '__generated__/ChatThreadActions_Thread.graphql'
import { ChatThreadActions_User$key } from '__generated__/ChatThreadActions_User.graphql'
import { ChatThreadActions_SendMutation } from '__generated__/ChatThreadActions_SendMutation.graphql'
import { ChatThreadActions_Recipient$key } from '__generated__/ChatThreadActions_Recipient.graphql'
import { trackError } from 'services/analytics'
import { updateCurrentUserInStore } from 'api/graphql'
import { useTheme } from '@mui/material/styles'
import { IconButton } from '@mui/material'
import AddReactionIcon from '@mui/icons-material/AddReaction'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { PickerFileMetadata } from 'filestack-js'
import ButtonStackSpacer from 'components/ButtonStackSpacer'
import { Nullable } from 'types'
import { isMobile } from 'services/browserAgent'
import ChatThreadEditDialog from '../ChatThreadEditDialog'

interface IChatThreadActionsProps {
  threadFragmentRef?: Nullable<ChatThreadActions_Thread$key>
  userFragmentRef: ChatThreadActions_User$key
  recipientFragmentRef?: Nullable<ChatThreadActions_Recipient$key>
  messageLabel: string
  messagePlaceholder?: string
  disableGroupChat?: boolean
  largeChatMode?: boolean
  autoFocus?: boolean
}

function ChatThreadActions({ disableGroupChat, messageLabel, messagePlaceholder, threadFragmentRef, userFragmentRef, recipientFragmentRef, largeChatMode, autoFocus = true }: IChatThreadActionsProps) {
  const theme = useTheme()
  const { conversation, defaultMessage, setConversation, jobId } = useChat()
  const [message, setMessage] = useState(defaultMessage || '')
  const [contactsOpen, setContactsOpen] = useState(false)
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
  const handleEmojiCLick = (emojiData: EmojiClickData) => {
    setMessage(message + emojiData.emoji)
    setEmojiPickerOpen(false)
  }

  const filestackAccept = [
    'text/plain',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/*',
  ]

  const handleFileUpload = (file: PickerFileMetadata) => {
    if (file.mimetype.includes('image')) {
      setMessage(`${message} ![${file.filename}](${file.url})`)
    } else {
      setMessage(`${message} [${file.filename}](${file.url})`)
    }
  }

  const filestack = useUploadFile({
    onChange: file => handleFileUpload(file),
    options: {
      accept: filestackAccept,
    },
  })

  const user = useFragment(graphql`
    fragment ChatThreadActions_User on User {
      contacts(first: 1) {
        totalCount
      }
    }
  `, userFragmentRef)

  const thread = useFragment(graphql`
    fragment ChatThreadActions_Thread on ChatThread {
      id
      threadType
      purpose

      ...ChatThreadEditDialog_Thread

      recipient {
        ...ChatThreadActions_Recipient
      }
    }
  `, threadFragmentRef)

  const recipient = useFragment(graphql`
    fragment ChatThreadActions_Recipient on Recipient {
      ... on User {
        id
      }

      ... on ChatBot {
        id
      }

      ...ChatThreadEditDialog_Recipient
    }
  `, (recipientFragmentRef || thread?.recipient) as ChatThreadActions_Recipient$key)

  const { execute, loading } = useQuickCommit<ChatThreadActions_SendMutation>(graphql`
    mutation ChatThreadActions_SendMutation($input: SendChatMessageInput!) {
      sendChatMessage(input: $input) {
        chatMessage {
          message

          ...ChatMessage_Message

          chatThread {
            id

            # preload data for the dialog, in case this is the first message that spawns a thread
            ...ChatDialogContent_Thread

            # update the list of messages in the thread. This is needed in some cases
            ...ChatThreadMessages_Thread

            # update the list of users in the thread. This is needed when sending a message to a thread
            # that the current user hasn't joined yet
            ...ChatThreadUsers_Thread
          }

          user {
            id # Use this to set currentUser in the relay state
            guest # needed to avoid flashing while this field is fetched
          }
        }
      }
    }
  `)

  const containerRef = useRef<HTMLDivElement>(null)

  const sendMessage = useCallback(() => {
    if (message.trim() !== '') {
      execute({
        input: {
          chatThreadId: thread?.id,
          recipientId: thread ? null : recipient?.id,
          message,
          chatAutomationId: conversation?.chatAutomationId,
          jobId,
        },
      }, {
        updater(store, result) {
          try {
            const threadId = result?.sendChatMessage?.chatMessage?.chatThread?.id
            if (threadId) {
              // Tell relay that optionalNode with this thread's id returns this thread
              // This would not be needed if we could change the ChatDialogContent query to use node() instead of
              // using optionalNode()
              // TODO: change optionalNode() to node()
              const threadRecord = store.get(threadId)
              if (threadRecord) {
                store.getRoot().setLinkedRecord(threadRecord, 'optionalNode', { id: threadId })
              }
            }
          } catch (error) {
            trackError(error)
          }

          try {
            const currentUserId = result?.sendChatMessage?.chatMessage?.user?.id
            if (currentUserId) {
              // Tell relay that the user has logged in with this ID. This is needed because if the message is sent
              // while logged out, the user will be auto logged in as guest
              updateCurrentUserInStore(store, currentUserId)
            }
          } catch (error) {
            trackError(error)
          }
        },
      }).then((result) => {
        if (result?.sendChatMessage?.chatMessage?.message) {
          setMessage('')
        }

        if (!thread?.id) {
          // If this is the first message that starts a thread, set it as currently opened
          startTransition(() => {
            setConversation({ id: result?.sendChatMessage?.chatMessage?.chatThread?.id })
          })
        }

        setTimeout(() => {
          containerRef?.current?.scrollIntoView()
        }, 0)
      })
    }
  }, [message, thread])

  const enableGroupChat = !disableGroupChat && (thread?.threadType === 'group' || user?.contacts?.totalCount > 1 || thread?.purpose)

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.which === 13 && (e.ctrlKey || e.metaKey)) {
      sendMessage()
    }
  }, [sendMessage])

  const handleMessageChange = useCallback((event) => {
    setMessage(event.target.value)
  }, [setMessage])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (autoFocus) {
        containerRef?.current?.scrollIntoView()
      }
    }, 100)

    return () => {
      clearTimeout(timeout)
    }
  }, [autoFocus])

  useEffect(() => {
    setMessage('')
    setContactsOpen(false)
    setEmojiPickerOpen(false)
  }, [thread?.id, recipient?.id])

  if (!recipient && !thread) {
    // Always need a DOM container for the initial auto-scroll to work
    return (
      <div ref={containerRef} />
    )
  }

  if (largeChatMode) {
    return (
      <div ref={containerRef}>
        <div style={{ display: 'flex' }}>
          {contactsOpen && (
            <ChatThreadEditDialog
              threadFragmentRef={thread}
              recipientFragmentRef={recipient}
              onClose={() => setContactsOpen(false)}
            />
          )}

          {emojiPickerOpen && (
            <ResponsiveDialog open scroll="paper" onClose={() => setEmojiPickerOpen(false)}>
              <EmojiPicker lazyLoadEmojis onEmojiClick={handleEmojiCLick} />
            </ResponsiveDialog>
          )}

          {enableGroupChat && (
            <IconButton color="primary" onClick={() => setContactsOpen(true)} style={{ marginRight: theme.spacing(2) }}>
              <People />
            </IconButton>
          )}

          <TextArea
            minRows={4}
            fullWidth
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            autoFocus={!isMobile() && autoFocus}
            disabled={loading}
            label={messageLabel}
            placeholder={messagePlaceholder}
            name="message"
          />
        </div>

        <div style={{ display: 'flex', paddingTop: theme.spacing(2) }}>
          <ButtonStack style={{ marginLeft: 'auto' }}>
            <ButtonStackSpacer />

            <IconButton color="primary" onClick={(filestack.openPicker)}>
              <AttachFileIcon />
            </IconButton>

            <IconButton color="primary" onClick={() => { setEmojiPickerOpen(true) }}>
              <AddReactionIcon />
            </IconButton>

            <IconButton color="primary" onClick={sendMessage} disabled={loading} data-cy="send" data-testid="send">
              <Send />
            </IconButton>
          </ButtonStack>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex' }} ref={containerRef}>
      {contactsOpen && (
        <ChatThreadEditDialog
          threadFragmentRef={thread}
          recipientFragmentRef={recipient}
          onClose={() => setContactsOpen(false)}
        />
      )}

      {emojiPickerOpen && (
        <ResponsiveDialog open scroll="paper" onClose={() => setEmojiPickerOpen(false)}>
          <EmojiPicker lazyLoadEmojis onEmojiClick={handleEmojiCLick} />
        </ResponsiveDialog>
      )}

      {enableGroupChat && (
        <IconButton color="primary" onClick={() => setContactsOpen(true)} style={{ marginRight: theme.spacing(2) }}>
          <People />
        </IconButton>
      )}

      <TextArea
        minRows={1}
        fullWidth
        value={message}
        onChange={handleMessageChange}
        onKeyDown={handleKeyDown}
        autoFocus={!isMobile() && autoFocus}
        disabled={loading}
        label={messageLabel}
        name="message"
      />

      <IconButton color="primary" onClick={(filestack.openPicker)} style={{ marginRight: theme.spacing(2), marginLeft: theme.spacing(2) }}>
        <AttachFileIcon />
      </IconButton>

      <IconButton color="primary" onClick={() => { setEmojiPickerOpen(true) }} style={{ marginRight: theme.spacing(2) }}>
        <AddReactionIcon />
      </IconButton>

      <IconButton color="primary" onClick={sendMessage} disabled={loading} data-cy="send" data-testid="send">
        <Send />
      </IconButton>
    </div>
  )
}

export default memo(ChatThreadActions)
